import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  ArrowLeft, Edit2, Save, Trash2, Send, CheckCircle, 
  X, Plus, Loader2, FileText, Download, EyeOff, MessageCircle,
  AlertTriangle, Info, Receipt, Mail, Search, BookmarkPlus, Package
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const QuoteDetail = ({ user, API }) => {
  const navigate = useNavigate();
  const { quoteId } = useParams();
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Edit state
  const [editedQuote, setEditedQuote] = useState(null);
  
  // Materials search state
  const [savedMaterials, setSavedMaterials] = useState([]);
  const [materialSearch, setMaterialSearch] = useState("");
  const [showMaterialSearch, setShowMaterialSearch] = useState(null);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  
  useEffect(() => {
    fetchQuote();
  }, [quoteId]);
  
  // Filter materials based on search
  useEffect(() => {
    if (materialSearch.length > 1) {
      const filtered = savedMaterials.filter(m => 
        m.name.toLowerCase().includes(materialSearch.toLowerCase()) ||
        m.category?.toLowerCase().includes(materialSearch.toLowerCase())
      );
      setFilteredMaterials(filtered.slice(0, 10));
    } else {
      setFilteredMaterials([]);
    }
  }, [materialSearch, savedMaterials]);
  
  const fetchQuote = async () => {
    try {
      const response = await fetch(`${API}/quotes/${quoteId}`, {
        credentials: "include"
      });
      if (!response.ok) throw new Error("Quote not found");
      const data = await response.json();
      setQuote(data);
      setEditedQuote(data);
    } catch (error) {
      toast.error("Failed to load quote");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch saved materials
  const fetchMaterials = async () => {
    try {
      const response = await fetch(`${API}/materials`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSavedMaterials(data);
      }
    } catch (error) {
      console.error("Failed to load materials:", error);
    }
  };
  
  // Select a material from search results
  const selectMaterial = (index, material) => {
    const newItems = [...editedQuote.items];
    newItems[index] = {
      ...newItems[index],
      description: material.name,
      unit_price: material.price,
      unit_price_ex_vat: material.price / 1.2,
      from_saved_materials: true
    };
    setEditedQuote({ ...editedQuote, items: newItems });
    setShowMaterialSearch(null);
    setMaterialSearch("");
    toast.success(`Selected: ${material.name}`);
  };
  
  // Save an item to materials database
  const saveToMaterials = async (item) => {
    try {
      const response = await fetch(`${API}/materials`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: item.description,
          price: item.unit_price,
          category: "Quote Items"
        })
      });
      
      if (response.ok) {
        toast.success(`"${item.description}" saved to your materials`);
        fetchMaterials(); // Refresh materials list
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save material");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/quotes/${quoteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          customer_name: editedQuote.customer_name,
          customer_email: editedQuote.customer_email,
          customer_phone: editedQuote.customer_phone,
          customer_address: editedQuote.customer_address,
          job_description: editedQuote.job_description,
          items: editedQuote.items,
          labor_hours: editedQuote.labor_hours,
          status: editedQuote.status
        })
      });
      
      if (!response.ok) throw new Error("Failed to save");
      
      const updated = await response.json();
      setQuote(updated);
      setEditedQuote(updated);
      setIsEditing(false);
      toast.success("Quote saved successfully");
    } catch (error) {
      toast.error("Failed to save quote");
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this quote?")) return;
    
    try {
      const response = await fetch(`${API}/quotes/${quoteId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (!response.ok) throw new Error("Failed to delete");
      
      toast.success("Quote deleted");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to delete quote");
    }
  };
  
  const updateStatus = async (newStatus) => {
    try {
      const response = await fetch(`${API}/quotes/${quoteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) throw new Error("Failed to update status");
      
      const updated = await response.json();
      setQuote(updated);
      setEditedQuote(updated);
      toast.success(`Quote marked as ${newStatus}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };
  
  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${API}/quotes/${quoteId}/pdf`, {
        credentials: "include"
      });
      
      if (!response.ok) throw new Error("Failed to generate PDF");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Quote_${quoteId.slice(-8)}_${quote?.customer_name?.replace(/\s+/g, '_') || 'Customer'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF: " + error.message);
    } finally {
      setDownloading(false);
    }
  };

  const [sharing, setSharing] = useState(false);
  
  // Email state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleSendEmail = async () => {
    if (!emailAddress || !emailAddress.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await fetch(`${API}/quotes/${quoteId}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          quote_id: quoteId,
          recipient_email: emailAddress,
          message: emailMessage || null
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Failed to send email");
      }
      
      toast.success(`Quote sent to ${emailAddress}`);
      setShowEmailModal(false);
      setEmailAddress("");
      setEmailMessage("");
      
      // Refresh quote to get updated status
      fetchQuote();
    } catch (error) {
      toast.error(error.message || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const shareWhatsApp = async () => {
    if (!quote) return;
    
    setSharing(true);
    try {
      // Fetch the PDF
      const response = await fetch(`${API}/quotes/${quoteId}/pdf`, {
        credentials: "include"
      });
      
      if (!response.ok) throw new Error("Failed to generate PDF");
      
      const blob = await response.blob();
      const fileName = `Quote_${quoteId.slice(-8)}_${quote?.customer_name?.replace(/\s+/g, '_') || 'Customer'}.pdf`;
      
      // Create a File object from the blob
      const file = new File([blob], fileName, { type: 'application/pdf' });
      
      // Check if Web Share API with files is supported
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Quote for ${quote.customer_name}`,
          text: `Hi ${quote.customer_name}, please find attached your quote for £${quote.total?.toFixed(2)}. Let me know if you have any questions!`
        });
        toast.success("Quote shared successfully");
      } else {
        // Fallback: Download PDF and show instructions
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.info("PDF downloaded. Open WhatsApp and attach the file to send.", {
          duration: 5000
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled the share
        return;
      }
      toast.error("Failed to share: " + error.message);
    } finally {
      setSharing(false);
    }
  };
  
  const addItem = () => {
    setEditedQuote({
      ...editedQuote,
      items: [
        { description: "", quantity: 1, unit_price: 0 },
        ...editedQuote.items
      ]
    });
  };
  
  const updateItem = (index, field, value) => {
    const newItems = [...editedQuote.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setEditedQuote({ ...editedQuote, items: newItems });
  };
  
  const removeItem = (index) => {
    setEditedQuote({
      ...editedQuote,
      items: editedQuote.items.filter((_, i) => i !== index)
    });
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-secondary text-secondary-foreground",
      sent: "bg-blue-500/20 text-blue-400",
      accepted: "bg-green-500/20 text-green-400",
      rejected: "bg-destructive/20 text-destructive"
    };
    return styles[status] || styles.draft;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }
  
  const currentQuote = isEditing ? editedQuote : quote;
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold">Quote #{quote?.quote_id?.slice(-6)}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={getStatusBadge(currentQuote?.status)}>
                    {currentQuote?.status}
                  </Badge>
                  {/* Confidence indicator - not shown to customers */}
                  {quote?._confidence !== undefined && (
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${
                        quote._confidence >= 90 
                          ? 'border-green-500/50 text-green-400' 
                          : quote._confidence >= 70 
                            ? 'border-amber-500/50 text-amber-400'
                            : 'border-red-500/50 text-red-400'
                      }`}
                    >
                      {quote._confidence}% confidence
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEditedQuote(quote);
                      setIsEditing(false);
                    }}
                    data-testid="cancel-edit-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-primary text-primary-foreground"
                    data-testid="save-btn"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    onClick={downloadPDF}
                    disabled={downloading}
                    data-testid="download-pdf-btn"
                  >
                    {downloading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    PDF
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setIsEditing(true);
                      fetchMaterials(); // Load saved materials when editing
                    }}
                    data-testid="edit-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleDelete}
                    className="text-destructive"
                    data-testid="delete-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        <div className="space-y-6">
          {/* Customer Info */}
          <Card className="bg-card border-white/5">
            <CardHeader>
              <CardTitle className="text-lg">Customer Information</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer Name</Label>
                    <Input
                      value={editedQuote?.customer_name || ""}
                      onChange={(e) => setEditedQuote({...editedQuote, customer_name: e.target.value})}
                      className="bg-input/50 border-white/10"
                      data-testid="edit-customer-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={editedQuote?.customer_email || ""}
                      onChange={(e) => setEditedQuote({...editedQuote, customer_email: e.target.value})}
                      className="bg-input/50 border-white/10"
                      data-testid="edit-customer-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={editedQuote?.customer_phone || ""}
                      onChange={(e) => setEditedQuote({...editedQuote, customer_phone: e.target.value})}
                      className="bg-input/50 border-white/10"
                      data-testid="edit-customer-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={editedQuote?.customer_address || ""}
                      onChange={(e) => setEditedQuote({...editedQuote, customer_address: e.target.value})}
                      className="bg-input/50 border-white/10"
                      data-testid="edit-customer-address"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{currentQuote?.customer_name}</p>
                  </div>
                  {currentQuote?.customer_email && (
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{currentQuote?.customer_email}</p>
                    </div>
                  )}
                  {currentQuote?.customer_phone && (
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium">{currentQuote?.customer_phone}</p>
                    </div>
                  )}
                  {currentQuote?.customer_address && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-muted-foreground">Address</p>
                      <p className="font-medium">{currentQuote?.customer_address}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Job Description */}
          <Card className="bg-card border-white/5">
            <CardHeader>
              <CardTitle className="text-lg">Job Description</CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <textarea
                  value={editedQuote?.job_description || ""}
                  onChange={(e) => setEditedQuote({...editedQuote, job_description: e.target.value})}
                  className="w-full min-h-[100px] p-3 bg-input/50 border border-white/10 rounded-md resize-none"
                  data-testid="edit-job-description"
                />
              ) : (
                <p className="whitespace-pre-wrap">{currentQuote?.job_description}</p>
              )}
            </CardContent>
          </Card>

          {/* Clarifying Questions (if confidence < 90%) */}
          {quote?._needs_clarification && quote?._clarifying_questions?.length > 0 && (
            <Card className="bg-amber-500/10 border-amber-500/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-400">
                  <AlertTriangle className="w-5 h-5" />
                  Additional Details Needed
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  We are {quote._confidence}% confident in this quote. Answering these questions would help:
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {quote._clarifying_questions.map((question, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 bg-background/50 rounded">
                      <span className="text-amber-400 font-bold">{idx + 1}.</span>
                      <span className="text-foreground">{question}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Edit the job description or line items above to provide more details, then regenerate the quote.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Line Items */}
          <Card className="bg-card border-white/5">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Line Items</CardTitle>
              {isEditing && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={addItem}
                  data-testid="add-item-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isEditing && (
                <p className="text-xs text-muted-foreground mb-3 flex items-center gap-2">
                  <Info className="w-3 h-3" />
                  Click <Search className="w-3 h-3 inline" /> to select from your saved materials, or <BookmarkPlus className="w-3 h-3 inline" /> to save an item
                </p>
              )}
              <div className="space-y-3">
                {currentQuote?.items?.map((item, index) => (
                  <div 
                    key={index} 
                    className={`p-4 rounded-lg ${item.needs_review ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-secondary/30'}`}
                    data-testid={`line-item-${index}`}
                  >
                    {isEditing ? (
                      <div className="space-y-3">
                        {/* Item number header */}
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-primary">Item {index + 1}</span>
                          <div className="flex items-center gap-1">
                            {/* Save to materials button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => saveToMaterials(item)}
                              title="Save to your materials"
                              className="text-muted-foreground hover:text-primary h-8"
                            >
                              <BookmarkPlus className="w-4 h-4 mr-1" />
                              <span className="text-xs">Save</span>
                            </Button>
                            
                            {/* Delete button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(index)}
                              className="text-destructive h-8"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              <span className="text-xs">Remove</span>
                            </Button>
                          </div>
                        </div>
                        
                        {/* Description field with label */}
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Description</Label>
                          <div className="relative">
                            <Input
                              value={item.description}
                              onChange={(e) => updateItem(index, "description", e.target.value)}
                              placeholder="Item description"
                              className="bg-input/50 border-white/10 pr-10"
                            />
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                              onClick={() => {
                                setShowMaterialSearch(showMaterialSearch === index ? null : index);
                                setMaterialSearch("");
                              }}
                              title="Search saved materials"
                            >
                              <Search className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Quantity and Price side by side */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Quantity</Label>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)}
                              className="bg-input/50 border-white/10"
                              min="0"
                              step="0.5"
                              placeholder="Qty"
                            />
                          </div>
                          
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Unit Price (£)</Label>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                              <Input
                                type="number"
                                value={item.unit_price}
                                onChange={(e) => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                                className="bg-input/50 border-white/10 pl-7"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Line total */}
                        <div className="flex justify-between items-center pt-2 border-t border-white/10">
                          <span className="text-sm text-muted-foreground">Line Total:</span>
                          <span className="text-lg font-bold text-primary">
                            £{((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}
                          </span>
                        </div>
                        
                        {/* Material search dropdown */}
                        {showMaterialSearch === index && (
                          <div className="bg-card border border-white/10 rounded-lg p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <Package className="w-4 h-4 text-muted-foreground" />
                              <Input
                                value={materialSearch}
                                onChange={(e) => setMaterialSearch(e.target.value)}
                                placeholder="Search your saved materials..."
                                className="bg-input/50 border-white/10 text-sm"
                                autoFocus
                              />
                            </div>
                            {filteredMaterials.length > 0 ? (
                              <div className="max-h-40 overflow-y-auto space-y-1">
                                {filteredMaterials.map((mat, mIdx) => (
                                  <button
                                    key={mIdx}
                                    className="w-full text-left px-3 py-2 rounded hover:bg-white/10 flex justify-between items-center text-sm"
                                    onClick={() => selectMaterial(index, mat)}
                                  >
                                    <span>{mat.name}</span>
                                    <span className="text-primary font-medium">£{mat.price?.toFixed(2)}</span>
                                  </button>
                                ))}
                              </div>
                            ) : materialSearch.length > 1 ? (
                              <p className="text-xs text-muted-foreground py-2 text-center">No materials found</p>
                            ) : (
                              <p className="text-xs text-muted-foreground py-2 text-center">Type to search your materials...</p>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Item header with description */}
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="font-medium text-foreground">
                              {item.description}
                              {item.needs_review && (
                                <AlertTriangle className="w-4 h-4 text-amber-500 inline ml-2" />
                              )}
                            </p>
                            {!item.exact_match && (
                              <span className="text-xs text-amber-400">(Alternative found)</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Price breakdown */}
                        <div className="grid grid-cols-3 gap-4 pt-2 border-t border-white/10">
                          <div>
                            <p className="text-xs text-muted-foreground">Quantity</p>
                            <p className="font-medium">{item.quantity}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Unit Price</p>
                            <p className="font-medium mono">
                              {quote?.show_inc_vat !== false 
                                ? formatCurrency(item.unit_price) 
                                : formatCurrency(item.unit_price_ex_vat || item.unit_price / 1.2)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Line Total</p>
                            <p className="font-bold text-primary mono text-lg">
                              {quote?.show_inc_vat !== false
                                ? formatCurrency(item.quantity * item.unit_price)
                                : formatCurrency(item.quantity * (item.unit_price_ex_vat || item.unit_price / 1.2))}
                            </p>
                          </div>
                        </div>
                        
                        {/* Price comparison row */}
                        {(item.screwfix_price || item.wolseley_price) && (
                          <div className="pt-2 border-t border-white/5 flex flex-wrap items-center gap-4 text-xs">
                            {item.screwfix_price && (
                              <span className="text-muted-foreground">
                                Screwfix: <span className="text-foreground mono">
                                  {quote?.show_inc_vat !== false
                                    ? `£${item.screwfix_price.toFixed(2)}`
                                    : `£${(item.screwfix_price_ex_vat || item.screwfix_price / 1.2).toFixed(2)}`
                                  }
                                </span>
                              </span>
                            )}
                            {item.wolseley_price && (
                              <span className="text-muted-foreground">
                                Wolseley: <span className="text-foreground mono">
                                  {quote?.show_inc_vat !== false
                                    ? `£${item.wolseley_price.toFixed(2)}`
                                    : `£${(item.wolseley_price_ex_vat || item.wolseley_price / 1.2).toFixed(2)}`
                                  }
                                </span>
                              </span>
                            )}
                            {item.price_variance && (
                              <span className={`ml-auto ${item.needs_review ? 'text-amber-500 font-medium' : 'text-muted-foreground'}`}>
                                {item.price_variance.toFixed(1)}% variance
                                {item.price_variance > 15 && ' ⚠️'}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Suggested alternative */}
                        {item.suggested_alternative && !item.exact_match && (
                          <div className="pt-2 border-t border-white/5 text-xs text-amber-400 flex items-center gap-2">
                            <Info className="w-3 h-3" />
                            <span>Alternative: {item.suggested_alternative}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                
                {(!currentQuote?.items || currentQuote.items.length === 0) && (
                  <p className="text-muted-foreground text-center py-4">No items added</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Labor */}
          <Card className="bg-card border-white/5">
            <CardHeader>
              <CardTitle className="text-lg">Labor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                <span>Labor Hours</span>
                {isEditing ? (
                  <Input
                    type="number"
                    value={editedQuote?.labor_hours || 0}
                    onChange={(e) => setEditedQuote({...editedQuote, labor_hours: parseFloat(e.target.value) || 0})}
                    className="w-24 bg-input/50 border-white/10"
                    min="0"
                    step="0.5"
                    data-testid="edit-labor-hours"
                  />
                ) : (
                  <span className="mono">{currentQuote?.labor_hours} hours @ {formatCurrency(currentQuote?.hourly_rate)}/hr</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Totals */}
          <Card className="bg-card border-white/5">
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Materials</span>
                  <span className="mono">
                    {quote?.show_inc_vat !== false
                      ? formatCurrency(quote?.materials_total)
                      : formatCurrency((quote?.materials_total || 0) / 1.2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Labor</span>
                  <span className="mono">
                    {quote?.show_inc_vat !== false
                      ? formatCurrency(quote?.labor_total)
                      : formatCurrency((quote?.labor_total || 0) / 1.2)}
                  </span>
                </div>
                {/* Emergency Callout Fee - only show if applicable */}
                {quote?.is_emergency && quote?.emergency_callout_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-500" />
                      Emergency Callout
                    </span>
                    <span className="mono text-orange-500">
                      {quote?.show_inc_vat !== false
                        ? formatCurrency(quote?.emergency_callout_fee)
                        : formatCurrency((quote?.emergency_callout_fee || 0) / 1.2)}
                    </span>
                  </div>
                )}
                <Separator className="bg-border" />
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="mono">
                    {quote?.show_inc_vat !== false
                      ? formatCurrency(quote?.subtotal)
                      : formatCurrency((quote?.subtotal || 0) / 1.2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Margin ({quote?.margin_percentage}%)</span>
                    {quote?.hide_margin && (
                      <Badge variant="secondary" className="text-xs">
                        <EyeOff className="w-3 h-3 mr-1" />
                        Hidden on PDF
                      </Badge>
                    )}
                  </div>
                  <span className="mono">
                    {quote?.show_inc_vat !== false
                      ? formatCurrency(quote?.margin_amount)
                      : formatCurrency((quote?.margin_amount || 0) / 1.2)}
                  </span>
                </div>
                <Separator className="bg-border" />
                <div className="flex justify-between text-xl font-bold">
                  <span>Total {quote?.show_inc_vat !== false ? '(inc. VAT)' : '(ex. VAT)'}</span>
                  <span className="mono text-primary">
                    {quote?.show_inc_vat !== false
                      ? formatCurrency(quote?.total)
                      : formatCurrency((quote?.total || 0) / 1.2)}
                  </span>
                </div>
                {quote?.show_inc_vat !== false && (
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>VAT Amount (20%)</span>
                    <span className="mono">{formatCurrency((quote?.total || 0) - (quote?.total || 0) / 1.2)}</span>
                  </div>
                )}
              </div>
              
              {/* VAT toggle */}
              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="show-vat" className="text-sm font-medium flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      Show prices including VAT
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Toggle to view prices with or without VAT
                    </p>
                  </div>
                  <Switch
                    id="show-vat"
                    checked={quote?.show_inc_vat !== false}
                    onCheckedChange={async (checked) => {
                      try {
                        const response = await fetch(`${API}/quotes/${quoteId}`, {
                          method: "PUT",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ show_inc_vat: checked })
                        });
                        if (!response.ok) throw new Error("Failed to update");
                        const updated = await response.json();
                        setQuote(updated);
                        setEditedQuote(updated);
                        toast.success(checked ? "Showing prices with VAT" : "Showing prices without VAT");
                      } catch (error) {
                        toast.error("Failed to update setting");
                      }
                    }}
                    data-testid="vat-toggle"
                  />
                </div>
              </div>
              
              {/* Hide margin toggle */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="hide-margin" className="text-sm font-medium">Hide margin on customer PDF</Label>
                    <p className="text-xs text-muted-foreground">
                      Margin will not appear on the PDF sent to customers
                    </p>
                  </div>
                  <Switch
                    id="hide-margin"
                    checked={quote?.hide_margin || false}
                    onCheckedChange={async (checked) => {
                      try {
                        const response = await fetch(`${API}/quotes/${quoteId}`, {
                          method: "PUT",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ hide_margin: checked })
                        });
                        if (!response.ok) throw new Error("Failed to update");
                        const updated = await response.json();
                        setQuote(updated);
                        setEditedQuote(updated);
                        toast.success(checked ? "Margin will be hidden on PDF" : "Margin will show on PDF");
                      } catch (error) {
                        toast.error("Failed to update setting");
                      }
                    }}
                    data-testid="hide-margin-toggle"
                  />
                </div>
                
                {/* Hide Item Prices Toggle */}
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="space-y-0.5">
                    <Label htmlFor="hide-item-prices" className="text-sm font-medium">
                      Hide Item Prices
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Only show total price on customer PDF (hide individual line prices)
                    </p>
                  </div>
                  <Switch
                    id="hide-item-prices"
                    checked={quote?.hide_item_prices || false}
                    onCheckedChange={async (checked) => {
                      try {
                        const response = await fetch(`${API}/quotes/${quoteId}`, {
                          method: "PUT",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ hide_item_prices: checked })
                        });
                        if (!response.ok) throw new Error("Failed to update");
                        const updated = await response.json();
                        setQuote(updated);
                        setEditedQuote(updated);
                        toast.success(checked ? "Item prices will be hidden on PDF" : "Item prices will show on PDF");
                      } catch (error) {
                        toast.error("Failed to update setting");
                      }
                    }}
                    data-testid="hide-item-prices-toggle"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          {!isEditing && (
            <div className="space-y-4">
              {/* PDF Download, Email & WhatsApp */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Button
                  onClick={downloadPDF}
                  disabled={downloading}
                  variant="secondary"
                  className="h-12"
                  data-testid="download-pdf-full-btn"
                >
                  {downloading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5 mr-2" />
                      Download PDF
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={() => {
                    setEmailAddress(quote?.customer_email || "");
                    setShowEmailModal(true);
                  }}
                  className="h-12 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="email-quote-btn"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Email Quote
                </Button>
                
                <Button
                  onClick={shareWhatsApp}
                  disabled={sharing}
                  className="h-12 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="share-whatsapp-btn"
                >
                  {sharing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Preparing...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </>
                  )}
                </Button>
              </div>
              
              {/* Status Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                {quote?.status === "draft" && (
                  <Button
                    onClick={() => updateStatus("sent")}
                    className="flex-1 h-12 bg-primary text-primary-foreground btn-glow"
                    data-testid="mark-sent-btn"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Mark as Sent
                  </Button>
                )}
                {quote?.status === "sent" && (
                  <>
                    <Button
                      onClick={() => updateStatus("accepted")}
                      className="flex-1 h-12 bg-green-600 hover:bg-green-700"
                      data-testid="mark-accepted-btn"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Mark Accepted
                    </Button>
                    <Button
                      onClick={() => updateStatus("rejected")}
                      variant="destructive"
                      className="flex-1 h-12"
                      data-testid="mark-rejected-btn"
                    >
                      <X className="w-5 h-5 mr-2" />
                      Mark Rejected
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      
      {/* Email Quote Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Email Quote
            </DialogTitle>
            <DialogDescription>
              Send this quote to your customer via email with PDF attachment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Customer Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="customer@example.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="bg-input/50 border-white/10"
                data-testid="email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message">Personal Message (optional)</Label>
              <Textarea
                id="message"
                placeholder="Add a personal note to your customer..."
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                className="bg-input/50 border-white/10 min-h-[100px]"
                data-testid="email-message-input"
              />
            </div>
            
            <div className="bg-secondary/30 p-3 rounded-lg text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Email will include:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Professional HTML quote summary</li>
                <li>PDF attachment with full details</li>
                <li>Your company branding</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowEmailModal(false)}
              disabled={sendingEmail}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailAddress}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuoteDetail;
